import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { contactInfo } from '../data/mockData';
import { useTranslation } from 'react-i18next';

export const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.message) {
      toast.error(t('contact.form.name') + ', ' + t('contact.form.email') + ', ' + t('contact.form.message'));
      return;
    }

    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Error al enviar el mensaje');
      }

      const data = await response.json();
      
      toast.success(t('contact.success.title'), {
        description: t('contact.success.message')
      });
      
      // Reset form
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error submitting contact form:', error);
      }
      toast.error('Error', {
        description: t('contact.form.message')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#C5A35C]/10 border border-[#C5A35C]/20 mb-6">
            <span className="text-[#C5A35C] text-sm font-semibold">{t('contact.badge')}</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            {t('contact.title')} <span className="text-[#C5A35C]">{t('contact.title.highlight')}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('contact.description')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="p-8 border-2 border-gray-100 shadow-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              {t('contact.form.title')}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.form.name')} *
                </label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('contact.form.name')}
                  required
                  className="border-2 border-gray-200 focus:border-[#C5A35C] transition-colors"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.form.email')} *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('contact.form.email')}
                  required
                  className="border-2 border-gray-200 focus:border-[#C5A35C] transition-colors"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.form.phone')}
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder=""
                  className="border-2 border-gray-200 focus:border-[#C5A35C] transition-colors"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.form.message')} *
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('contact.form.message')}
                  rows={5}
                  required
                  className="border-2 border-gray-200 focus:border-[#C5A35C] transition-colors resize-none"
                />
              </div>

              <Button 
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="w-full bg-[#C5A35C] hover:bg-[#B8933D] text-white text-lg py-6 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
                <Send className="ml-2 h-5 w-5" />
              </Button>
            </form>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6">
            <Card className="p-6 border-2 border-gray-100 hover:border-[#C5A35C]/30 transition-all duration-300 hover:shadow-lg">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-[#C5A35C]/10">
                  <Phone className="w-6 h-6 text-[#C5A35C]" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">{t('contact.info.phone')}</h4>
                  <p className="text-gray-600">{contactInfo.phone}</p>
                  <p className="text-sm text-gray-500 mt-1">{t('contact.info.whatsapp')}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 border-2 border-gray-100 hover:border-[#C5A35C]/30 transition-all duration-300 hover:shadow-lg">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-[#C5A35C]/10">
                  <Mail className="w-6 h-6 text-[#C5A35C]" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">{t('contact.info.email')}</h4>
                  <p className="text-gray-600">{contactInfo.email}</p>
                  <p className="text-sm text-gray-500 mt-1">{t('contact.info.email.response')}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 border-2 border-gray-100 hover:border-[#C5A35C]/30 transition-all duration-300 hover:shadow-lg">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-[#C5A35C]/10">
                  <Clock className="w-6 h-6 text-[#C5A35C]" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">{t('contact.info.hours')}</h4>
                  <p className="text-gray-600">{t('contact.info.hours.weekday')}</p>
                  <p className="text-sm text-gray-500 mt-1">{t('contact.info.hours.saturday')}</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
